/**
 * Critères de facturation d'une source et filtres de la liste de facturation
 * (DEV-621, écran M2, recette du 15/10).
 *
 * INTENTION. Dans la liste de facturation, le client lit pour chaque source
 * les trois critères qui décident si elle est facturée (sur le compte,
 * ajoutée pendant la période, mise à jour pendant la période), si elle l'est,
 * et ce que ferait sa suppression : libérer sa place tout de suite, ou rester
 * facturée jusqu'à la fin de la période. Un bandeau de filtres, avec le nombre
 * de sources par filtre, isole par exemple les sources qui libèrent une place.
 *
 * L'écran ne recalcule rien : il lit le bloc `criteres` et le bloc
 * `si_supprimee` servis par `GET /company/billing/source`, et les `compteurs`
 * servis avec la page. La liste est paginée côté serveur : les filtres partent
 * en paramètres de requête. Sans bloc servi, la liste garde ses colonnes
 * d'avant ; sans compteur servi, aucun nombre n'est écrit (jamais 0).
 *
 * Règles de rédaction : « facturée », « sur votre compte », « ajoutée le »,
 * aucun nom de marque, aucun détail technique, dates à l'heure de Paris.
 */
import { formatDate } from "@core/helpers"
import {
    REPONSES_ARBRE,
    type DevenirPlace,
    type LigneArbre,
} from "@core/composables/useCompanyPlafond"

// ================================
// CRITÈRES SERVIS
// ================================

/** Origine de la dernière mise à jour : relance par le client, ou donnée reçue */
export type OrigineMiseAJour = "client" | "donnee" | null

/** Bloc `criteres` servi par source */
export type CriteresServis = {
    sur_le_compte: boolean
    ajoutee_le: string | null
    ajoutee_dans_la_periode: boolean
    mise_a_jour_le: string | null
    /**
     * `null` : non mesurée (lecture de la donnée en échec, hors délai ou
     * au-delà des sources mesurées par appel) ; on ne dit ni oui ni non
     */
    mise_a_jour_dans_la_periode: boolean | null
    origine_mise_a_jour: OrigineMiseAJour
    supprimee_le: string | null
}

const dateOuNull = (x: unknown): string | null =>
    typeof x === "string" && x !== "" ? x : null

/**
 * Lit le bloc `criteres` servi ; `null` s'il est absent, si `sur_le_compte`
 * ou `ajoutee_dans_la_periode` n'est pas un booléen, ou si
 * `mise_a_jour_dans_la_periode` n'est ni un booléen ni `null` (la ligne garde
 * alors ses colonnes d'avant). `mise_a_jour_dans_la_periode: null` est servi
 * quand la donnée n'a pas pu être mesurée : il est gardé tel quel.
 * @param brut - `source.criteres` brut
 */
export function lireCriteres(brut: unknown): CriteresServis | null {
    if (!brut || typeof brut !== "object") return null
    const c = brut as Record<string, unknown>
    if (
        typeof c.sur_le_compte !== "boolean" ||
        typeof c.ajoutee_dans_la_periode !== "boolean" ||
        (typeof c.mise_a_jour_dans_la_periode !== "boolean" &&
            c.mise_a_jour_dans_la_periode !== null)
    ) {
        return null
    }
    return {
        sur_le_compte: c.sur_le_compte,
        ajoutee_le: dateOuNull(c.ajoutee_le),
        ajoutee_dans_la_periode: c.ajoutee_dans_la_periode,
        mise_a_jour_le: dateOuNull(c.mise_a_jour_le),
        mise_a_jour_dans_la_periode: c.mise_a_jour_dans_la_periode,
        origine_mise_a_jour:
            c.origine_mise_a_jour === "client" ||
            c.origine_mise_a_jour === "donnee"
                ? c.origine_mise_a_jour
                : null,
        supprimee_le: dateOuNull(c.supprimee_le),
    }
}

// ================================
// CELLULES
// ================================

/** Badge « pendant la période » des colonnes d'ajout et de mise à jour */
export const BADGE_PENDANT_LA_PERIODE = "pendant la période"

/**
 * Cellule « Sur votre compte » : « Oui » ou « Supprimée le JJ/MM/AAAA » ;
 * `etat` et `date` servent l'affichage sur deux lignes (« Supprimée », puis
 * « le JJ/MM/AAAA »)
 */
export function celluleSurLeCompte(c: CriteresServis): {
    texte: string
    surLeCompte: boolean
    etat: string
    date: string | null
} {
    if (c.sur_le_compte) {
        return { texte: "Oui", surLeCompte: true, etat: "Oui", date: null }
    }
    const le = formatDate(c.supprimee_le, "DD/MM/YYYY", "")
    return {
        texte: le ? `Supprimée le ${le}` : "Supprimée",
        surLeCompte: false,
        etat: "Supprimée",
        date: le ? `le ${le}` : null,
    }
}

/** Cellule « Ajoutée le » : date JJ/MM/AAAA (vide si non servie) et badge */
export function celluleAjoutee(c: CriteresServis): {
    date: string
    ceMoisCi: boolean
} {
    return {
        date: formatDate(c.ajoutee_le, "DD/MM/YYYY", ""),
        ceMoisCi: c.ajoutee_dans_la_periode,
    }
}

/** Mise à jour sans objet : source ajoutée pendant la période, sans date */
export const AJOUTEE_PENDANT_LA_PERIODE = "Ajoutée pendant la période"

/** Texte d'une mesure absente : jamais « Non », jamais 0 */
export const NON_MESURE = "Non mesuré"

/**
 * Cellule « Mise à jour le », lue du bloc servi :
 * - source ajoutée pendant la période : « Ajoutée pendant la période »
 *   (sans objet), qu'une date soit servie ou non ;
 * - date servie : JJ/MM/AAAA ;
 * - sans date, mise à jour dans la période (source supprimée dont une donnée
 *   est arrivée pendant la période, la date n'est pas conservée) :
 *   « Pendant la période » ;
 * - sans date, `mise_a_jour_dans_la_periode` faux : « Non » ;
 * - `mise_a_jour_dans_la_periode` null : « Non mesuré » (`nonMesure` vrai).
 * Le badge « pendant la période » suit `mise_a_jour_dans_la_periode` ; l'origine en
 * clair (« Par vous », « Donnée reçue ») sert l'infobulle.
 */
export function celluleMiseAJour(c: CriteresServis): {
    texte: string
    ceMoisCi: boolean
    origine: string | null
    nonMesure: boolean
    /**
     * Vrai : source ajoutée pendant la période (avec ou sans date de mise à
     * jour servie) ; la mise à jour est sans objet (facturée de toute façon),
     * comme dans le tableau du décompte : ni Oui, ni Non, ni infobulle
     */
    sansObjet: boolean
} {
    const date = formatDate(c.mise_a_jour_le, "DD/MM/YYYY", "")
    let origine: string | null = null
    if (c.origine_mise_a_jour === "client") origine = "Par vous"
    if (c.origine_mise_a_jour === "donnee") origine = "Donnée reçue"
    const dans = c.mise_a_jour_dans_la_periode
    // ajoutée pendant la période : facturée de toute façon, la mise à jour
    // est sans objet, avec ou sans date servie (demande du 16/10)
    if (c.ajoutee_dans_la_periode) {
        return {
            texte: AJOUTEE_PENDANT_LA_PERIODE,
            ceMoisCi: false,
            origine: null,
            nonMesure: false,
            sansObjet: true,
        }
    }
    if (dans === null && !date) {
        return {
            texte: NON_MESURE,
            ceMoisCi: false,
            origine: null,
            nonMesure: true,
            sansObjet: false,
        }
    }
    let texte = date
    if (!texte) texte = dans ? "Pendant la période" : "Non"
    return {
        texte,
        ceMoisCi: dans === true,
        origine: texte === "Non" ? null : origine,
        nonMesure: false,
        sansObjet: false,
    }
}

/**
 * Texte court de la colonne « Mise à jour » : « le JJ/MM », « Non »,
 * « Non mesuré », « Ajoutée pendant la période » (toute source ajoutée
 * pendant la période), ou « Pendant la période » (source supprimée dont la
 * date n'est pas conservée)
 */
export function texteColonneMiseAJour(c: CriteresServis): string {
    const cellule = celluleMiseAJour(c)
    const le = formatDate(c.mise_a_jour_le, "DD/MM", "")
    return le && !cellule.nonMesure && !cellule.sansObjet
        ? `le ${le}`
        : cellule.texte
}

/** Bloc `si_supprimee` servi pour une source sur le compte */
export type SiSupprimeeServi = {
    comptee: boolean
    comptee_jusqu_au: string | null
    motif?: string
}

/**
 * Cellule « Si vous la supprimez » : libère sa place (`libere` vrai), reste
 * facturée (`libere` faux), ou non mesurée (`libere` null)
 */
export type CelluleSiSupprimee = {
    texte: string
    libere: boolean | null
}

/**
 * Cellule « Si vous la supprimez », lue du seul bloc servi :
 * - `comptee` faux : « Libère sa place dès la suppression » ;
 * - `comptee` vrai : « Reste facturée jusqu'au JJ/MM » (`comptee_jusqu_au`,
 *   sinon la fin de période servie), « Reste facturée jusqu'à la fin de la
 *   période » sans date ;
 * - bloc absent, null ou illisible pour une source SUR LE COMPTE (critères
 *   servis) : « Non mesuré » (la mesure a échoué, rien n'est promis) ;
 * - sinon (source déjà supprimée, ou critères non servis) : `null`, cellule
 *   vide.
 * @param si - `source.si_supprimee` brut
 * @param periodeFin - fin de la période servie par le bloc `plafond`, si connue
 * @param surLeCompte - `criteres.sur_le_compte` servi, si connu
 */
export function celluleSiSupprimee(
    si: unknown,
    periodeFin?: string | null,
    surLeCompte?: boolean | null
): CelluleSiSupprimee | null {
    const nonMesure =
        surLeCompte === true ? { texte: NON_MESURE, libere: null } : null
    if (!si || typeof si !== "object") return nonMesure
    const { comptee, comptee_jusqu_au } = si as Partial<SiSupprimeeServi>
    if (comptee === false) {
        return { texte: "Libère sa place dès la suppression", libere: true }
    }
    if (comptee !== true) return nonMesure
    const fin =
        formatDate(comptee_jusqu_au, "DD/MM", "") ||
        formatDate(periodeFin, "DD/MM", "")
    return {
        texte: fin
            ? `Reste facturée jusqu'au ${fin}`
            : "Reste facturée jusqu'à la fin de la période",
        libere: false,
    }
}

/** Cellule « Sa place se libère » : tout de suite (`libre`), à la fin, ou non mesurée */
export type CellulePlace = {
    texte: string
    libre: boolean | null
    /** Devenir de la place (couleur de l'étiquette), `null` non mesuré */
    devenir: DevenirPlace | null
}

/**
 * Devenir de la place d'une source sur le compte, lu de la cellule « Si vous
 * la supprimez » : libère sa place ou resterait facturée ; `null` non mesuré
 * @param si - cellule « Si vous la supprimez »
 */
export function devenirSiSupprimee(
    si: CelluleSiSupprimee | null
): DevenirPlace | null {
    if (!si || si.libere === null) return null
    return si.libere ? "liberent" : "restent"
}

/**
 * Cellule « Sa place se libère », mots du tableau du décompte
 * (`REPONSES_ARBRE.place`) :
 * - sur le compte, libère sa place : « dès la suppression » ;
 * - sur le compte, resterait facturée : « le JJ/MM, si vous la supprimez » ;
 * - sur le compte, sort non mesuré : « Non mesuré » ;
 * - supprimée et facturée : « le JJ/MM » ;
 * - supprimée et non facturée : « libérée dès la suppression ».
 * @param surLeCompte - `criteres.sur_le_compte` servi
 * @param si - cellule « si vous la supprimez » lue du bloc servi
 * @param facturee - facturée sur la période (jugement servi)
 * @param periodeFin - fin de la période servie, si connue
 */
export function cellulePlace(
    surLeCompte: boolean,
    si: CelluleSiSupprimee | null,
    facturee: boolean,
    periodeFin?: string | null
): CellulePlace {
    const fin = formatDate(periodeFin, "DD/MM", "") || null
    const P = REPONSES_ARBRE.place
    if (surLeCompte) {
        const devenir = devenirSiSupprimee(si)
        if (!si || devenir === null) {
            return { texte: NON_MESURE, libre: null, devenir: null }
        }
        return si.libere
            ? { texte: P.desSuppression, libre: true, devenir }
            : { texte: P.siSupprimee(fin), libre: false, devenir }
    }
    return facturee
        ? { texte: P.fin(fin), libre: false, devenir: "supprimees_facturees" }
        : { texte: P.liberee, libre: true, devenir: "non_facturee" }
}

// ================================
// FILTRES
// ================================

/** Paramètre de requête d'un filtre, et ses valeurs */
export type ParametreFiltre =
    | "sur_le_compte"
    | "ajoutee_periode"
    | "maj_periode"
    | "facturee"
    | "si_supprimee"

/** Filtre du bandeau : un paramètre de requête et sa valeur */
export type FiltreBandeau = {
    id: string
    libelle: string
    parametre: ParametreFiltre
    valeur: string
}

/**
 * Filtres du bandeau, dans l'ordre d'affichage (« Toutes » les retire tous).
 * Deux filtres du même paramètre s'excluent ; des paramètres différents se
 * combinent.
 */
export const FILTRES_BANDEAU: readonly FiltreBandeau[] = [
    {
        id: "sur_le_compte",
        libelle: "Sur votre compte",
        parametre: "sur_le_compte",
        valeur: "oui",
    },
    {
        id: "supprimees",
        libelle: "Supprimées",
        parametre: "sur_le_compte",
        valeur: "non",
    },
    {
        id: "ajoutees",
        libelle: "Ajoutées pendant la période",
        parametre: "ajoutee_periode",
        valeur: "oui",
    },
    {
        id: "ajoutees_avant",
        libelle: "Ajoutées avant la période",
        parametre: "ajoutee_periode",
        valeur: "non",
    },
    {
        id: "mises_a_jour",
        libelle: "Mises à jour pendant la période",
        parametre: "maj_periode",
        valeur: "oui",
    },
    {
        id: "non_mises_a_jour",
        libelle: "Non mises à jour pendant la période",
        parametre: "maj_periode",
        valeur: "non",
    },
    {
        id: "facturees",
        libelle: "Facturées",
        parametre: "facturee",
        valeur: "oui",
    },
    {
        id: "non_facturees",
        libelle: "Non facturées",
        parametre: "facturee",
        valeur: "non",
    },
    {
        id: "liberent",
        libelle: "Libèrent une place",
        parametre: "si_supprimee",
        valeur: "libere",
    },
    {
        id: "restent",
        libelle: "Restent facturées",
        parametre: "si_supprimee",
        valeur: "reste",
    },
]

/** Filtres actifs : une valeur par paramètre */
export type FiltresActifs = Partial<Record<ParametreFiltre, string>>

const PARAMETRES: readonly ParametreFiltre[] = [
    "sur_le_compte",
    "ajoutee_periode",
    "maj_periode",
    "facturee",
    "si_supprimee",
]

/** Vrai si le filtre est actif */
export function estActif(actifs: FiltresActifs, f: FiltreBandeau): boolean {
    return actifs[f.parametre] === f.valeur
}

/**
 * Bascule un filtre : actif, il se retire ; sinon il remplace la valeur du
 * même paramètre et se combine aux autres.
 */
export function basculerFiltre(
    actifs: FiltresActifs,
    f: FiltreBandeau
): FiltresActifs {
    const suivants = { ...actifs }
    if (estActif(actifs, f)) delete suivants[f.parametre]
    else suivants[f.parametre] = f.valeur
    return suivants
}

/**
 * Filtres lus de l'URL (`?si_supprimee=libere`) : seules les valeurs d'un
 * filtre du bandeau sont retenues.
 * @param query - `route.query`
 */
export function filtresDepuisUrl(
    query: Record<string, unknown> | null | undefined
): FiltresActifs {
    const actifs: FiltresActifs = {}
    if (!query) return actifs
    for (const p of PARAMETRES) {
        const brut = query[p]
        const v = Array.isArray(brut) ? brut[0] : brut
        if (
            typeof v === "string" &&
            FILTRES_BANDEAU.some((f) => f.parametre === p && f.valeur === v)
        ) {
            actifs[p] = v
        }
    }
    return actifs
}

/**
 * Filtre « Libèrent une place » seul (DEV-2480, bouton « Libérer de
 * l'espace » de la carte) : le même état que le lien des courriels d'alerte
 * (`?si_supprimee=libere`), lu du bandeau
 */
export function filtresLiberent(): FiltresActifs {
    const f = FILTRES_BANDEAU.find((x) => x.id === "liberent")
    return f ? { [f.parametre]: f.valeur } : {}
}

/** Vrai si au moins un filtre du bandeau est actif */
export function aDesFiltres(actifs: FiltresActifs): boolean {
    return Object.keys(actifs).length > 0
}

/**
 * Compteurs servis : `{ [parametre]: { [valeur]: nombre } }` (valeur
 * `inconnu` pour `maj_periode` et `si_supprimee` : non mesurées), plus
 * `total` (avant les filtres de critères) et `filtrees` (après), `null` s'ils
 * ne sont pas servis.
 */
export type CompteursServis = Partial<
    Record<ParametreFiltre, Record<string, number>>
> & {
    total?: number | null
    filtrees?: number | null
    /**
     * Sources sur le compte dont la mesure manque (mise à jour ou sort si
     * supprimée), servi par l'API depuis b9398004 ; `null` s'il ne l'est pas
     */
    non_mesurees?: number | null
}

const entier = (x: unknown): number | null =>
    typeof x === "number" && Number.isInteger(x) && x >= 0 ? x : null

/**
 * Lit les `compteurs` servis avec la page ; `null` s'ils sont absents.
 * Une entrée illisible est écartée (aucun nombre n'est alors écrit).
 */
export function lireCompteurs(brut: unknown): CompteursServis | null {
    if (!brut || typeof brut !== "object") return null
    const c = brut as Record<string, unknown>
    const lus: CompteursServis = {
        total: entier(c.total),
        filtrees: entier(c.filtrees),
        non_mesurees: entier(c.non_mesurees),
    }
    for (const p of PARAMETRES) {
        const parValeur = c[p]
        if (!parValeur || typeof parValeur !== "object") continue
        const valeurs: Record<string, number> = {}
        for (const [v, n] of Object.entries(parValeur)) {
            if (typeof n === "number" && Number.isFinite(n) && n >= 0) {
                valeurs[v] = n
            }
        }
        lus[p] = valeurs
    }
    return lus
}

/** Page servie, pour le repli de pagination */
export type PageServie = {
    /** Vrai si un filtre de critère est posé */
    filtresCriteres: boolean
    page: number
    limit: number
    /** Sources servies sur la page */
    servies: number
    /** `has_more` servi */
    aSuivante: boolean
}

/**
 * Nombre de sources à paginer :
 * - `filtrees` servi : la liste filtrée, jugée sur l'ensemble ;
 * - sinon, avec un filtre de critère posé : ce qui est servi (sources déjà
 *   parcourues, page courante, une de plus s'il y a une page suivante), car
 *   `count=true` ignore ces filtres et donnerait un total faux ;
 * - sinon `null` : l'appelant peut lire `count=true` (aucun filtre de critère).
 */
export function totalAPaginer(
    compteurs: CompteursServis | null,
    page: PageServie
): number | null {
    if (typeof compteurs?.filtrees === "number") return compteurs.filtrees
    if (!page.filtresCriteres) return null
    return (
        (Math.max(1, page.page) - 1) * page.limit +
        page.servies +
        (page.aSuivante ? 1 : 0)
    )
}

/**
 * Vrai si la page doit demander `compteurs=oui` : la première page (chaque
 * changement de filtre revient à la première page) ou tant qu'aucun compteur
 * n'a été lu ; les pages suivantes gardent les compteurs déjà lus.
 */
export function demanderCompteurs(
    page: number,
    compteursLus: CompteursServis | null
): boolean {
    return page <= 1 || compteursLus === null
}

/**
 * Filtres de l'URL d'arrivée sur la page Entreprise (lien des courriels
 * d'alerte de plafond : `/user/entreprise?si_supprimee=libere#subscriptions`) :
 * les filtres du bandeau retenus (une valeur inconnue est ignorée) et la
 * section à atteindre, `subscriptions` dès qu'un filtre est posé, même sans
 * ancre ; `null` sans filtre (l'ancre éventuelle garde son effet).
 * @param query - `route.query`
 */
export function arriveeDepuisUrl(
    query: Record<string, unknown> | null | undefined
): { filtres: FiltresActifs; section: "subscriptions" } | null {
    const filtres = filtresDepuisUrl(query)
    return aDesFiltres(filtres) ? { filtres, section: "subscriptions" } : null
}

// ================================
// LARGEURS DES COLONNES (demande du 16/10)
// ================================

/**
 * Largeurs des colonnes à contenu borné de la liste avec critères, en rem
 * (`table-layout: fixed`), dans l'ordre des colonnes après Source (reprise
 * du 16/10, « petits problèmes d'alignement sur les petits écrans » : à
 * 1280 px barre latérale ouverte, 846 px de conteneur, « Ajoutée le » à
 * 110 px coupait sa date) :
 * - EA : Ajoutée le 8, Sur votre compte 10, Mise à jour 9, Facturée 7, Sa
 *   place se libère 12 ;
 * - EM : les mêmes sans Mise à jour.
 * La colonne Source prend le reste (`auto`), au moins `SOURCE_MIN_REM`.
 */
export const LARGEURS_REM = {
    EA: [8, 10, 9, 7, 12],
    EM: [8, 10, 7, 12],
} as const

/** Largeur minimale de la colonne Source, en rem */
export const SOURCE_MIN_REM = 14

/** Largeur de la colonne Actions (demandes en attente), en rem */
export const ACTIONS_REM = 14

/**
 * Disposition compacte (mobile, ou conteneur plus étroit que le seuil) :
 * Source, Facturée, Sa place se libère, en pourcentage de somme 100
 */
export const LARGEURS_COMPACTES = [40, 24, 36] as const

/** Part de la colonne Actions en disposition compacte, en pourcentage */
export const PART_ACTIONS = 20

/**
 * Seuil de la disposition complète, en rem : Source au minimum plus les
 * colonnes bornées (EA 60, EM 51), plus la colonne Actions si elle est là.
 * Sous ce seuil de largeur de conteneur, la liste passe en disposition
 * compacte au lieu de se tasser ou de défiler.
 * @param offre - offre servie (`EM` sans colonne « Mise à jour »)
 * @param avecActions - colonne Actions présente
 */
export function seuilCompletRem(
    offre: "EA" | "EM" | null,
    avecActions: boolean
): number {
    const bornees = offre === "EM" ? LARGEURS_REM.EM : LARGEURS_REM.EA
    return (
        SOURCE_MIN_REM +
        bornees.reduce((n, x) => n + x, 0) +
        (avecActions ? ACTIONS_REM : 0)
    )
}

/**
 * Largeurs servies au tableau, en valeurs CSS, dans l'ordre des colonnes
 * (Actions comprise) :
 * - disposition complète : Source `auto`, puis les largeurs en rem de
 *   l'offre, puis Actions ;
 * - disposition compacte : pourcentages de somme 100 ; avec la colonne
 *   Actions, les autres sont réduites à proportion pour lui laisser sa part
 *   (calcul au dixième, le reste d'arrondi va à la première colonne).
 * @param offre - offre servie (`EM` sans colonne « Mise à jour »)
 * @param compacte - disposition compacte
 * @param avecActions - colonne Actions présente
 */
export function largeursColonnes(
    offre: "EA" | "EM" | null,
    compacte: boolean,
    avecActions: boolean
): string[] {
    if (!compacte) {
        const bornees = offre === "EM" ? LARGEURS_REM.EM : LARGEURS_REM.EA
        return [
            "auto",
            ...bornees.map((x) => `${x}rem`),
            ...(avecActions ? [`${ACTIONS_REM}rem`] : []),
        ]
    }
    const base: readonly number[] = LARGEURS_COMPACTES
    if (!avecActions) return base.map((x) => `${x}%`)
    // en dixièmes de pour cent, pour une somme exacte
    const reste = (100 - PART_ACTIONS) * 10
    const dixiemes = base.map((x) => Math.floor((x * reste) / 100))
    dixiemes[0] += reste - dixiemes.reduce((n, x) => n + x, 0)
    return [...dixiemes.map((x) => `${x / 10}%`), `${PART_ACTIONS}%`]
}

/**
 * Première réponse courte coupée à sa virgule, chaque morceau insécable :
 * « Oui, jusqu'au 12/11 » tient sur deux lignes au plus, jamais coupé dans
 * la date ; « Oui » reste d'un bloc
 * @param texte - réponse courte
 */
export function morceauxInsecables(texte: string): string[] {
    const i = texte.indexOf(", ")
    return i < 0 ? [texte] : [texte.slice(0, i + 1), texte.slice(i + 2)]
}

/** Nom de la route de la page « Sources facturées » (`/user/entreprise/sources`) */
export const ROUTE_SOURCES_FACTUREES = "PageCompanySources"

/**
 * Lien de courriel vers la page Entreprise portant un filtre de critères
 * (`/user/entreprise?si_supprimee=libere#subscriptions`) : la liste est
 * désormais une page, on y mène avec la même query (sans l'ancre) ; `null`
 * sans filtre de critères (la page Entreprise reste affichée).
 * @param query - `route.query`
 */
export function redirectionVersSources(
    query: Record<string, unknown> | null | undefined
): { name: string; query: Record<string, string> } | null {
    if (!arriveeDepuisUrl(query) || !query) return null
    const q: Record<string, string> = {}
    for (const [k, v] of Object.entries(query)) {
        const x = Array.isArray(v) ? v[0] : v
        if (typeof x === "string") q[k] = x
    }
    return { name: ROUTE_SOURCES_FACTUREES, query: q }
}

/** Nombre affiché d'un filtre du bandeau */
export type NombreFiltre = {
    /** « 40 », ou « 40 ou plus » quand des sources n'ont pas pu être mesurées */
    nombre: string
    /** « 2 non mesurées » à côté du nombre, `null` si tout est mesuré */
    nonMesurees: string | null
}

/**
 * Nombre d'un filtre tel qu'il s'écrit dans le bandeau. Pour `maj_periode` et
 * `si_supprimee`, le serveur sert `inconnu` (sources dont la mesure a
 * manqué) : le nombre n'est alors jamais écrit comme exact, « 40 ou plus »,
 * avec « 2 non mesurées » à côté (le trou se lit dans la donnée). `null`
 * quand le nombre n'est pas servi.
 */
export function nombreFiltre(
    compteurs: CompteursServis | null,
    f: FiltreBandeau
): NombreFiltre | null {
    const n = compteFiltre(compteurs, f)
    if (n === null) return null
    const inconnu = compteurs?.[f.parametre]?.inconnu
    if (typeof inconnu !== "number" || inconnu <= 0) {
        return { nombre: String(n), nonMesurees: null }
    }
    // total servi des sources non mesurées, sinon `inconnu` du filtre
    const servi = compteurs?.non_mesurees
    const x = typeof servi === "number" && servi > 0 ? servi : inconnu
    return {
        nombre: `${n} ou plus`,
        nonMesurees: x > 1 ? `${x} non mesurées` : "1 non mesurée",
    }
}

/** Nombre de sources servi pour un filtre, `null` s'il n'est pas servi */
export function compteFiltre(
    compteurs: CompteursServis | null,
    f: FiltreBandeau
): number | null {
    const n = compteurs?.[f.parametre]?.[f.valeur]
    return typeof n === "number" ? n : null
}

// ================================
// PASTILLES DE L'ARBRE (revue UX du 15/10)
// ================================

/**
 * Pastille de la liste : les filtres d'une ligne du tableau du décompte, sans
 * nombre (le seul nombre écrit est celui de la liste filtrée)
 */
export type PastilleArbre = {
    id: string
    /** Libellé complet (nom accessible de la pastille) */
    libelle: string
    /**
     * Texte affiché : sans le préfixe « Sur votre compte, » pour les pastilles
     * du groupe, que la rangée écrit une fois devant elles (une seule rangée)
     */
    court: string
    /** Groupe dont le préfixe est écrit une fois devant ses pastilles */
    groupe: "sur_le_compte" | null
    /** Filtres posés par la pastille ; `{}` pour « Toutes » */
    filtres: FiltresActifs
}

/** Vrai si les deux jeux de filtres sont exactement les mêmes */
export function memesFiltres(a: FiltresActifs, b: FiltresActifs): boolean {
    const ka = Object.keys(a) as ParametreFiltre[]
    const kb = Object.keys(b)
    return ka.length === kb.length && ka.every((k) => a[k] === b[k])
}

/**
 * Pastilles de la liste : exactement les lignes du tableau du décompte
 * (`lignesArbre`) qui portent un lien, avec leurs filtres, précédées de
 * « Toutes » ; en Energy Analytics, le groupe « ajoutées avant » (deux
 * sous-lignes sur le compte) a aussi sa pastille. Un nombre cliqué dans le
 * tableau active ainsi toujours sa pastille. Les sources supprimées non
 * facturées n'ont pas de pastille (la liste ne les sert pas).
 * @param lignes - lignes du tableau du décompte
 * @param debut - début de la période servie
 */
export function pastillesArbre(
    lignes: readonly LigneArbre[],
    debut?: string | Date | null
): PastilleArbre[] {
    const d = formatDate(debut, "DD/MM", "")
    const depuis = d ? `ajoutées depuis le ${d}` : "ajoutées pendant la période"
    type Brute = Omit<PastilleArbre, "court" | "groupe">
    const pastilles: Brute[] = [
        { id: "toutes", libelle: "Toutes", filtres: {} },
    ]
    const avant: Brute = {
        id: "ajoutees_avant",
        libelle: d
            ? `Sur votre compte, ajoutées avant le ${d}`
            : "Sur votre compte, ajoutées avant la période",
        filtres: { sur_le_compte: "oui", ajoutee_periode: "non" },
    }
    for (const ligne of lignes) {
        const filtres = ligne.filtres as FiltresActifs | null
        if (!filtres) continue
        const copie = { ...filtres }
        if (filtres.sur_le_compte === "non") {
            if (filtres.ajoutee_periode === "oui") {
                pastilles.push({
                    id: "supprimees_ajoutees",
                    libelle: `Supprimées, ${depuis}`,
                    filtres: copie,
                })
            } else if (filtres.maj_periode === "oui") {
                pastilles.push({
                    id: "supprimees_mises_a_jour",
                    libelle: "Supprimées, mises à jour pendant la période",
                    filtres: copie,
                })
            }
            continue
        }
        if (filtres.ajoutee_periode === "oui") {
            pastilles.push({
                id: "ajoutees",
                libelle: `Sur votre compte, ${depuis}`,
                filtres: copie,
            })
            continue
        }
        if (!pastilles.includes(avant)) pastilles.push(avant)
        if (filtres.maj_periode === "oui") {
            pastilles.push({
                id: "mises_a_jour",
                libelle: "Sur votre compte, mises à jour pendant la période",
                filtres: copie,
            })
        } else if (filtres.maj_periode === "non") {
            pastilles.push({
                id: "liberent",
                libelle:
                    "Sur votre compte, libèrent leur place dès la suppression",
                filtres: copie,
            })
        }
    }
    return pastilles.map((p) =>
        p.libelle.startsWith(PREFIXE_SUR_LE_COMPTE)
            ? {
                  ...p,
                  court: p.libelle.slice(PREFIXE_SUR_LE_COMPTE.length),
                  groupe: "sur_le_compte",
              }
            : { ...p, court: p.libelle, groupe: null }
    )
}

/** Préfixe des pastilles des sources sur le compte */
export const PREFIXE_SUR_LE_COMPTE = "Sur votre compte, "

/** Préfixe écrit une fois devant les pastilles du groupe */
export const TITRE_GROUPE_SUR_LE_COMPTE = "Sur votre compte :"

/**
 * Libellé de filtres actifs qui ne correspondent à aucune pastille (lien de
 * courriel `?si_supprimee=libere`) : les libellés du bandeau d'origine, dans
 * l'ordre des paramètres ; `null` sans filtre.
 */
export function libelleFiltresHorsPastilles(
    actifs: FiltresActifs
): string | null {
    const libelles = PARAMETRES.flatMap((p) => {
        const f = FILTRES_BANDEAU.find(
            (x) => x.parametre === p && x.valeur === actifs[p]
        )
        return f ? [f.libelle] : []
    })
    return libelles.length ? libelles.join(", ") : null
}
